import { Routes, Route, Link } from "react-router-dom";
import { useState } from "react";
import { DropdownDivider, DropdownItem } from "flowbite-react";
import { Layout, ProtectedRoute, AuthLoadingScreen, useAuthentication, useTranslation } from "@repo/core";
import { useProducts, useUserApi } from "@repo/api";
import Overview from "../pages/overview";
import StrategyDetailType from "../pages/strategy-detail";
import FactorMimicking from "../pages/factor-mimicking";
import Subscriptions from "../pages/subscriptions";
import Welcome from "../pages/welcome";
import Login from "../pages/Login";
import Signup from "../pages/Signup";
import ForgotPassword from "../pages/ForgotPassword";
import Unauthorized from "../pages/Unauthorized";
import Profile from "../pages/profile";
import PrivacyPolicy from "../pages/legal/PrivacyPolicy";
import TermsAndConditions from "../pages/legal/TermsAndConditions";
import { ProductTourProvider, useProductTour } from "../context/ProductTourContext";
import AddRoboMarketsKeyModal from "./ui/AddRoboMarketsKeyModal";
import SubscriptionUpgradeModal from "./ui/SubscriptionUpgradeModal";
import { useStripeCheckout } from "../hooks/useStripeCheckout";

const AppRoutes = () => {
  const { isLoading, user } = useAuthentication();
  const { sortedProducts } = useProducts();
  const { startTour } = useProductTour();
  const { createCustomerPortalSession } = useStripeCheckout();
  const { changeEmailSubscription } = useUserApi();
  const { t, tCommon } = useTranslation();
  const [showRoboMarketsKeyModal, setShowRoboMarketsKeyModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Show loading screen while checking authentication
  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  const renderExtraMenuItems = () => (
    <>
      <DropdownItem onClick={() => changeEmailSubscription(!user?.emailsSubscribed)}>
        {user?.emailsSubscribed ? tCommon("unsubscribeFromMailing") : tCommon("subscribeToMailing")}
      </DropdownItem>
      <DropdownItem href="/subscriptions">{tCommon("subscriptions")}</DropdownItem>
      {user?.stripeCustomerId && (
        <DropdownItem onClick={createCustomerPortalSession}>{t("navbar.subscriptionPortal")}</DropdownItem>
      )}
      {user && (
        <DropdownItem onClick={() => setShowRoboMarketsKeyModal(true)}>
          {t("navbar.addRoboMarketsKey")}
        </DropdownItem>
      )}
      {/* Legal documents - signed-in users reach them from the menu; anonymous
          visitors get the same links on the signup form. `as={Link}` keeps the
          navigation client-side instead of reloading the SPA. */}
      {user && (
        <>
          <DropdownDivider />
          <DropdownItem as={Link} to="/terms-and-conditions">
            {t("legal.termsAndConditions")}
          </DropdownItem>
          <DropdownItem as={Link} to="/privacy-policy">
            {t("legal.privacyPolicy")}
          </DropdownItem>
        </>
      )}
      <DropdownDivider />
      <DropdownItem onClick={startTour}>
        🎓 {t("navbar.startTour")}
      </DropdownItem>
    </>
  );

  const renderModals = () => (
    <>
      <AddRoboMarketsKeyModal
        isOpen={showRoboMarketsKeyModal}
        onClose={() => setShowRoboMarketsKeyModal(false)}
      />
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </>
  );

  return (
    <Routes>
      {/* Standalone route (no navbar) */}
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Protected + public-under-navbar routes */}
      <Route
        path="/"
        element={
          <Layout
            products={sortedProducts}
            renderExtraMenuItems={renderExtraMenuItems}
            profileHref="/profile"
            renderModals={renderModals}
            onSubscriptionBadgeClick={() => setShowUpgradeModal(true)}
          />
        }
      >
        {/* Login / signup / password reset - public, but rendered under the
            normal navbar */}
        <Route path="login" element={<Login />} />
        <Route path="signup" element={<Signup />} />
        <Route path="forgot-password" element={<ForgotPassword />} />

        {/* Legal documents - public, linked from the signup form */}
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="terms-and-conditions" element={<TermsAndConditions />} />

        {/* Dashboard - accessible to all authenticated users */}
        <Route
          index
          element={
            <ProtectedRoute
              requireAuth={true}
              fallbackRoute="/factor-mimicking"
            >
              <Overview />
            </ProtectedRoute>
          }
        />

        {/* Factor Mimicking - requires 'read' permission */}
        <Route path="factor-mimicking" element={<FactorMimicking />} />

        {/* Welcome page */}
        <Route path="welcome" element={<Welcome />} />

        {/* Subscriptions page */}
        <Route
          path="subscriptions"
          element={<Subscriptions />}
        />

        {/* Profile / account settings - signed-in users only */}
        <Route
          path="profile"
          element={
            <ProtectedRoute requireAuth={true}>
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* Strategy Detail - requires 'read' permission */}
        <Route
          path="draft"
          element={
            <ProtectedRoute requireAuth={true}>
              <StrategyDetailType />
            </ProtectedRoute>
          }
        />

        <Route
          path=":strategyId"
          element={
            <ProtectedRoute requireAuth={true}>
              <StrategyDetailType />
            </ProtectedRoute>
          }
        />

        <Route
          path=":strategyId/draft"
          element={
            <ProtectedRoute requireAuth={true}>
              <StrategyDetailType />
            </ProtectedRoute>
          }
        />

        {/* Delivered Strategy - requires 'read' permission */}
        <Route
          path="delivered/:universe/:dateRange/:strategyId"
          element={<StrategyDetailType />}
        />
      </Route>
    </Routes>
  );
};

const AppRouter = () => {
  // <Router> now lives in App.tsx, above AuthenticationProvider, so auth
  // logic can navigate to local routes (e.g. /login) itself.
  return (
    <ProductTourProvider>
      <AppRoutes />
    </ProductTourProvider>
  );
};

export default AppRouter;
